import { CustomAxiosHeaders } from '../config/services'
import { Location } from '../models'
import config from '../config'
import { requests } from '.'

// use serviceName value below as key in `versions` object in `src/config/services.ts` for developer versions
const serviceName = 'WordpressService'
const host = config.wordpress.apiUrl
const opts: CustomAxiosHeaders = { host, serviceName }
const request = (addlOpts?: CustomAxiosHeaders) => {
  return requests(Object.assign({}, opts, addlOpts))
}

const WordpressService = {
  Logs: {
    addAudit: (type, location: Location, changes) => {
      const data = {
        type: 'CREATED',
        companyId: location.companyId,
        locationName: location.name || 'Location',
        documentName: location.name,
        documentType: 'WORDPRESS',
        documentId: location.id,
        after: changes,
      }
      return request().post(`/log/audit/`, data)
    },
  },
  TroubleShooter: {
    generalFix: (locationId: string, fixType) => {
      return request().post(
        `/troubleshooter/general-fix/${locationId}`,
        {
          fixType,
        }
      )
    },
  },
  Payment: {
    getProduct: (companyId: string) => {
      return request().get(`/payments/company/${companyId}/product`)
    },
    purchaseWordpressWithCDP: (locationId: string) => {
      return request().post(`/payments/location/${locationId}/purchase/company_direct_purchase`)
    },
    purchaseWithAgencyDirectSubscription: async (locationId: string, userId: string, oldSubscriptionId: string) => {
      return request().post(
        `/payments/location/${locationId}/purchase/company_direct_purchase`,
        {
          meta: {
            userId,
            oldSubscriptionId,
          },
        }
      )
    },
    getPaymentStatus: (locationId: string) => {
      return request().get(`/payments/location/${locationId}/status`)
    },
    getCompanyConfig: (companyId: string) => {
      return request().get(`/payments/company/${companyId}/config`)
    },
    setCompanyConfig: (companyId: string, payload : object) => {
      return request().post(`/payments/company/${companyId}/config`, payload)
    },
    getLocationConfig: (locationId: string) => {
      return request().get(`/payments/location/${locationId}/config`)
    },
    setLocationConfig: (locationId: string, payload : object) => {
      return request().post(`/payments/location/${locationId}/config`, payload)
    },
    getCompanySubscriptions: (companyId: string) => {
      return request().get(`/payments/company/${companyId}/subscriptions`)
    },
    getLocationSubscriptions: (locationId: string) => {
      return request().get(`/payments/location/${locationId}/subscriptions`)
    },
    getLocationPurchaseConfig: (locationId: string) => {
      return request().get(`/payments/location/${locationId}/purchase/config`)
    },
    getLocationSiteLists: (locationId: string) => {
      return request().get(`/payments/location/${locationId}/list`)
    }
  },
  Common:{
    getWordpressByLocId: async (locationId: string) => {
      return request().get(`/${locationId}`)
    },
    createWpSite: async(siteData: object, locationId: string) => {
      return request().post(
        `/site/${locationId}`,
        siteData
      )
    },
    deleteWpSite: async (locationId: string) => {
      return request().delete(`/site/${locationId}`)
    },
    addDomain: async (locationId: string, domain: string) => {
      const data = {
        domain: domain,
      }
      return request().post(`/domain/${locationId}`, data )
    },
    verifyDns: async (locationId: string, domain: string) => {
      const data = {
        domain: domain,
      }
      return request().post(`/domain/${locationId}/verify-dns/`, data )
    },
    makeDomainPrimary: async (locationId: string, domain: string) => {
      const data = {
        domain: domain,
      }
      return request().post(`/domain/${locationId}/primary/`, data )
    },
    deleteSubDomain: async (locationId: string, domain: string) => {
      const data = {
        domain: domain,
      }
      return request().delete(`/domain/${locationId}`, {data} )
    },
    getWordpressUsers: async (locationId: string) => {
      return request().get(`/wp-users/${locationId}`)
    },
    createWordpressUser: async (locationId: string, data:Object) => {
      return request().post(`/wp-users/${locationId}`,data)
    },
    updateWordpressUser: async (locationId: string, data:Object) => {
      return request().patch(`/wp-users/${locationId}`, data)
    },
    updateWordpressUserPassword: async (locationId: string, data:Object) => {
      return request().patch(`/wp-users/${locationId}`,data)
    },
    deleteWordpressUser: async (locationId: string, data:Object) => {
      return request().delete(`/wp-users/${locationId}`,{data})
    },
    clearCacheWordpressUser: async (locationId: string) => {
      return request().post(`/site/${locationId}/clear-cache`)
    },
    requestMigration: async (locationId: string) => {
      return request().post(`/site/${locationId}/request-migration`)
    },
    accessPhpmyadminWordpressUser: async (locationId: string, data:Object) => {
      return request().post(`/site/${locationId}/phpmy-admin`, data)
    },
    updateRecords: async (locationId: string, domain: string, type: string) => {
      const data = {
        domain: domain,
        type: type
      }
      return request().post(`/domain/${locationId}/verify-dns-records/`, data )
    },
    changePrefix: async (locationId: string) => {
      return request().patch(`/domain/${locationId}/change-prefix` )
    },
    getDebugMode: async (locationId: string) => {
      return request().post(`/site/${locationId}/debug-mode`)
    },
    updateDebugMode: async (locationId: string, data:Object) => {
      return request().post(`/site/${locationId}/update-debug-mode`, data)
    }
  },
  Ftp:{
    generateFTPCredentials: async (locationId: string, data:Object) => {
      return request().post(`/ftp/${locationId}`, data)
    },
    getFTPCredentials: async (locationId: string) => {
      return request().get(`/ftp/${locationId}`)
    },
    deleteFTPCredentials: async (locationId: string, data:Object) => {
      return request().delete(`/ftp/${locationId}`, {data})
    },
    updateFTPCredentials: async (locationId: string, data:Object) => {
      return request().patch(`/ftp/${locationId}/update`, data)
    },
  },
  Backups:{
    restoreBackups: async (locationId: string, data:Object) => {
      return request().post(`/backup/${locationId}/restore`, data)
    },
    getAllBackups: async (locationId: string) => {
      return request().get(`/backup/${locationId}`)
    },
    UpdateRestoreBackups: async (locationId: string) => {
      return request().patch(`/backup/${locationId}/restore-status`)
    }
  },
  Plugins:{
    getAllPlugins: async (locationId: string) => {
      return request().get(`/plugin/${locationId}`)
    },
    changePluginStatus: async (locationId: string, data:Object) => {
      return request().patch(`/plugin/${locationId}/change-status`, data)
    },
    updatePlugin: async (locationId: string, data:Object) => {
      return request().put(`/plugin/${locationId}/update`, data)
    },
    deletePlugin: async (locationId: string, data:Object) => {
      return request().delete(`/plugin/${locationId}`, {data})
    },
  },
  Themes:{
    getAllThemes: async (locationId: string) => {
      return request().get(`/theme/${locationId}`)
    },
    changeThemesStatus: async (locationId: string, data:Object) => {
      return request().patch(`/theme/${locationId}/change-status`, data)
    },
    updateThemes: async (locationId: string, data:Object) => {
      return request().put(`/theme/${locationId}/update`, data)
    },
    deleteThemes: async (locationId: string, data:Object) => {
      return request().delete(`/theme/${locationId}`, {data})
    },
  },
  Migrator:{
    restoreBackup: async (locationId: string) => {
      return request().post(`/migrator/${locationId}`)
    },
    restoreBackupStatusCheck: async (locationId: string) => {
      return request().get(`/migrator/${locationId}/restore-status`)
    },
    restoreBackupUpdateDb: async (locationId: string, data:Object) => {
      return request().post(`/migrator/${locationId}/restore-updates`, data)
    },
    addNewBackupFile: async (locationId: string, data:Object) => {
      return request().post(`/migrator/${locationId}/add-backup-file`, data)
    },
    deleteBackupFile: async (locationId: string, fileName) => {
      return request().post(`/migrator/${locationId}/delete-backup-file`, {fileName})
    },
  },
  Staging:{
    createStaging: async (locationId: string) => {
      return request().post(`/staging/${locationId}`)
    },
    checkStagingStatus: async (locationId: string) => {
      return request().post(`/staging/${locationId}/staging-status`)
    },
    publishStaging: async (locationId: string) => {
      return request().post(`/staging/${locationId}/publish`)
    },
    checkPublishedStatus: async (locationId: string) => {
      return request().post(`/staging/${locationId}/publish-status`)
    },
    deleteStaging: async (locationId: string) => {
      return request().delete(`/staging/${locationId}`)
    },
    checkStagingInProcessTask: async (locationId: string) => {
      return request().get(`/staging/${locationId}`)
    },
  }
}
export default WordpressService
